import {
  MutationHandlingServiceV2,
  NotificationService,
  QueryHandlingService,
} from '@akebono/core';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  finalize,
  first,
  map,
  pluck,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ManualLotData, validateAllFormFields } from 'src/app/const';
import {
  BidCondition,
  BidCreateManualGQL,
  BidManualDataGQL,
  LotTypeEnum,
} from 'src/app/modules/graphql/service/graphql-auto-main-service';

import { BidConditionService } from '../../services/bid-condition.service';
import {
  MakersGQL,
  Maker,
  MarksGQL,
} from 'src/app/modules/graphql/service/graphql-shared-familiar-service';
import { Mark } from '@akebono/calculator/lib/graphql/directory/graphql-directory-service';

@Component({
  selector: 'app-bid-manual-modal',
  templateUrl: './bid-manual-modal.component.html',
  styleUrls: ['./bid-manual-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BidManualModalComponent implements OnInit, OnDestroy {
  conditions$: Observable<BidCondition[]>;
  loading$ = of(true);
  isPlacingBid$ = of(false);
  auctions$: Observable<string[]> = of([]);
  makers$: Observable<Maker[]>;
  filteredMarks$: Observable<Mark[]>;
  makersLoading$: Observable<boolean>;
  marksLoading$: Observable<boolean>;

  lotTypes = Object.values(LotTypeEnum);

  form: FormGroup;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public modal: NzModalRef,
    private qhs: QueryHandlingService,
    private mhs: MutationHandlingServiceV2,
    private notification: NotificationService,
    private bidConditionService: BidConditionService,
    private bidManualDataGQL: BidManualDataGQL,
    private bidCreateManualGQL: BidCreateManualGQL,
    private makersGQL: MakersGQL,
    private marksGQL: MarksGQL,
  ) {
    this.form = this.fb.group({
      lotType: [LotTypeEnum.Auto, [Validators.required]],
      date: [null, [Validators.required]],
      time: [null, [Validators.required]],
      auctionName: [null, [Validators.required]],
      code: [null, [Validators.required]],
      company: [null, [Validators.required]],
      modelName: [null],
      frame: [null, [Validators.required]],
      year: [null, [Validators.required]],
      amount: [null, [Validators.required]],
      startPrice: [null, [Validators.required]],
      conditionId: [{ value: null, disabled: true }, [Validators.required]],
      comment: [null, [Validators.required]],
      createTranslation: [false],
      notify: [false],
      displacementNum: [null],
    });
  }

  ngOnInit(): void {
    this.fetchMakers();

    const companyId$ = this.form.get('company')!.valueChanges.pipe(
      switchMap((makerName: string) => {
        if (!makerName) {
          return of(null);
        }
        return this.makers$.pipe(
          first(),
          map((makers) => makers.find((m) => m.name === makerName)?.id ?? null),
        );
      }),
      startWith(null),
      shareReplay(1),
    );

    const queryRef = this.qhs.fetch(this.bidManualDataGQL, {
      withUnassignedCartrackAuction: true,
    });

    this.loading$ = queryRef.loading;
    const data$ = queryRef.data.pipe(shareReplay(1));
    const currentUser$ = data$.pipe(map((data) => data.currentUser));

    const lotDataPartial$ = this.form.valueChanges.pipe(
      map(
        (formValue) =>
          <ManualLotData>{
            company: formValue.company,
            modelName: formValue.modelName,
            frame: formValue.frame,
            year: formValue.year,
            lotType: formValue.lotType,
          },
      ),
      shareReplay(1),
    );
    const lotDataRequired$ = lotDataPartial$.pipe(
      filter((lotData) => Object.values(lotData).every((value) => !!value)),
    );

    this.conditions$ = combineLatest([
      lotDataRequired$,
      currentUser$,
      data$.pipe(
        map((data) => data.bidConditions),
        map((conditions) => this.bidConditionService.sort(conditions)),
      ),
    ]).pipe(
      map(([lotData, currentUser, bidConditions]) =>
        this.bidConditionService.filterForManualLot(bidConditions, lotData, currentUser),
      ),
    );
    this.auctions$ = data$.pipe(
      map((data) => data.auctions ?? []),
      map((auctions) => auctions.map((auction) => auction.title)),
    );

    const marks$ = companyId$.pipe(
      distinctUntilChanged(),
      tap(() => {
        this.form.get('modelName')?.setValue(null, { emitEvent: false });
      }),
      switchMap((makerId: number) => {
        if (makerId) {
          return this.fetchModels(makerId);
        }
        return of([]);
      }),
      startWith([]),
      shareReplay(1),
      takeUntil(this.destroy$),
    );

    this.filteredMarks$ = combineLatest([
      marks$,
      this.form.get('modelName')!.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([marks, search]) => {
        const query = (search || '').toLowerCase().trim();
        return marks.filter((mark) => mark.name?.toLowerCase().includes(query));
      }),
      shareReplay(1),
    );

    lotDataPartial$
      .pipe(
        map((lotData) => Object.values(lotData).every((value) => !!value)),
        takeUntil(this.destroy$),
      )
      .subscribe((bidConditionEnabled) => {
        const conditionIfControl = this.form.controls.conditionId;
        if (bidConditionEnabled && conditionIfControl.disabled) {
          conditionIfControl.enable();
          conditionIfControl.setValue(null);
        } else if (!bidConditionEnabled && conditionIfControl.enabled) {
          conditionIfControl.disable();
          conditionIfControl.setValue(null);
        }
      });
  }

  fetchMakers(): void {
    const queryRef = this.qhs.fetch(this.makersGQL, {});
    this.makersLoading$ = queryRef.loading;
    this.makers$ = queryRef.data.pipe(
      pluck('makers'),
      map((makers) => [...makers].sort((a, b) => a.name?.localeCompare(b.name))),
      shareReplay(1),
    );
  }

  fetchModels(makerId: number): Observable<Mark[]> {
    const queryRef = this.qhs.fetch(this.marksGQL, { makerId });
    this.marksLoading$ = queryRef.loading;
    return queryRef.data.pipe(
      pluck('marks'),
      map((marks) => [...marks].sort((a, b) => a.name?.localeCompare(b.name)) || []),
    );
  }

  disabledYear = (current: Date): boolean => {
    return current ? current.getFullYear() < 1900 : false;
  };

  async placeBid(): Promise<void> {
    validateAllFormFields(this.form);

    if (this.form.valid) {
      if (Number(this.form.value.startPrice) % 5000 !== 0) {
        this.notification.renderError('LOT_BIDDING_MODAL.BID_MUST_BE_ROUND');
        return;
      }
      if (this.form.value.startPrice > this.form.value.amount) {
        this.notification.renderError('RATE_MUST_BE_HIGHER');
        return;
      }

      const year = this.form.value.year.getFullYear();

      const conditions = await this.conditions$.pipe(first()).toPromise();
      const conditionIdControl = this.form.controls.conditionId;
      if (!conditions.find((condition) => condition.id === conditionIdControl.value)) {
        this.notification.renderError('WRONG_BID_CONDITION');
        conditionIdControl.setValue(null);
        return;
      }

      const auctionDatetime = DateTime.fromJSDate(this.form.value.date)
        .set({
          hour: this.form.value.time.getHours(),
          minute: this.form.value.time.getMinutes(),
        })
        .startOf('minute')
        .setZone('Asia/Tokyo', { keepLocalTime: true })
        .toISO();

      this.isPlacingBid$ = this.mhs
        .mutate(
          this.bidCreateManualGQL,
          {
            input: {
              amount: this.form.value.amount,
              lotType: this.form.value.lotType,
              conditionId: this.form.value.conditionId,
              comment: this.form.value.comment,
              createTranslation: this.form.value.createTranslation,
              notify: this.form.value.notify,
              code: this.form.value.code.toString(),
              auctionName: this.form.value.auctionName.trim(),
              auctionDatetime: auctionDatetime,
              company: this.form.value.company,
              model: this.form.value.modelName,
              modelType: this.form.value.frame,
              year: year,
              startPrice: this.form.value.startPrice,
              displacementNum: this.form.value.displacementNum,
            },
          },
          {
            successTranslationKey: 'LOT_BIDDING_MODAL.SUCCESS_CREATE_BID',
            failureTranslationKey: 'LOT_BIDDING_MODAL.FAIL_CREATE_BID',
          },
        )
        .loading.pipe(finalize(() => this.modal.close()));
    } else {
      this.notification.renderError('LOT_BIDDING_MODAL.FILL_ALL_FIELDS');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
